import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { LetterGlasGroup, LetterGlasRail } from '@/components/ui/LetterGlasRail'
import { TeacherCode } from '@/components/teacher/TeacherCode'
import { useLetterGlas } from '@/hooks/useLetterGlas'

export type LetterDirectoryTeacher = {
  id: string
  last_name: string
  first_name: string
  teacher_code?: string | null
}

export function TeacherLetterDirectory<T extends LetterDirectoryTeacher>({
  items,
  query,
  onQueryChange,
  selectedId,
  onSelect,
  title,
  placeholder = 'Name oder Kürzel…',
  footer,
  compact = false,
}: {
  items: T[]
  query: string
  onQueryChange: (q: string) => void
  selectedId?: string | null
  onSelect: (item: T) => void
  title?: string
  placeholder?: string
  footer?: ReactNode
  compact?: boolean
}) {
  const lastNameOf = useCallback((t: T) => t.last_name, [])
  const {
    letter,
    setLetter,
    hoverLetter,
    setHoverLetter,
    focusLetter,
    have,
    letters,
    grouped,
  } = useLetterGlas(items, lastNameOf)
  const rootRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const namesOpen = !compact || hovered || pinned

  useEffect(() => {
    if (!pinned) return
    const close = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setPinned(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [pinned])

  const rail = (
    <LetterGlasRail
      letters={letters}
      have={have}
      letter={letter}
      hoverLetter={hoverLetter}
      focusLetter={focusLetter}
      onHover={setHoverLetter}
      onPick={setLetter}
    />
  )

  const names = (
    <>
      <input
        className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[13px] focus:border-blue-600 focus:outline-none focus:shadow-[0_0_0_3px_#dbeafe]"
        placeholder={placeholder}
        value={query}
        onChange={e => onQueryChange(e.target.value)}
      />
      <div className={`${compact ? 'mt-2.5 max-h-[min(480px,70vh)]' : 'mt-2.5 max-h-[480px]'} min-w-0 flex-1 overflow-auto`}>
        {grouped.length === 0 ? (
          <div className="px-1 py-7 text-center text-[13px] text-gray-400">Keine Treffer</div>
        ) : grouped.map(([L, rows]) => {
          const out = !!focusLetter && focusLetter !== L
          return (
            <LetterGlasGroup key={L} letter={L} hidden={out}>
              {rows.map(t => (
                <button
                  key={t.id}
                  type="button"
                  tabIndex={out ? -1 : undefined}
                  className={`flex w-full items-center gap-2.5 border-b border-gray-100 px-1.5 py-2 text-left ${selectedId === t.id ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                  onClick={() => { if (!out) onSelect(t) }}
                >
                  <TeacherCode code={t.teacher_code} />
                  <div className="min-w-0 truncate text-[13px] font-semibold">{t.last_name}, {t.first_name}</div>
                </button>
              ))}
            </LetterGlasGroup>
          )
        })}
      </div>
    </>
  )

  if (compact) {
    return (
      <div
        ref={rootRef}
        className="relative h-full"
        role="navigation"
        aria-label={title ?? 'Verzeichnis'}
        aria-expanded={namesOpen}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setPinned(true)}
      >
        <div className="flex h-full w-[42px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
          {rail}
        </div>
        {namesOpen ? (
          <div className="absolute left-[46px] top-0 z-30 flex w-[248px] flex-col rounded-xl border border-gray-200 bg-white p-3.5 shadow-lg">
            {title ? <div className="mb-2 text-[13px] font-semibold">{title}</div> : null}
            {names}
            {footer}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <>
      {title ? <div className="mb-2 text-[13px] font-semibold">{title}</div> : null}
      <div className="flex max-h-[480px] items-stretch gap-4">
        {rail}
        <div className="min-w-0 flex-1 overflow-hidden">
          {names}
        </div>
      </div>
      {footer}
    </>
  )
}
